import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ConsultationForm() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    guestCount: '',
    eventDate: '',
    budget: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.eventType) return;
    if (formData.message.length > 500) return;

    setStatus('submitting');
    try {
      const response = await fetch('https://readdy.ai/api/form/d6aoumma728k8ctu3ekg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          eventType: formData.eventType,
          guestCount: formData.guestCount,
          eventDate: formData.eventDate,
          budget: formData.budget,
          message: formData.message,
        }).toString(),
      });
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', eventType: '', guestCount: '', eventDate: '', budget: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
    setTimeout(() => setStatus('idle'), 5000);
  };

  return (
    <section id="consultation" className="py-28 bg-stone-50 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-300/30 to-transparent"></div>

      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left - Info */}
          <div
            ref={headerRef}
            className={`scroll-animate ${headerVisible ? 'visible' : ''}`}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-px bg-amber-400"></div>
              <span className="text-amber-600 text-xs font-semibold tracking-[0.2em] uppercase">
                Free Consultation
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-normal text-stone-900 mb-5">
              Let&apos;s Plan Your Event
            </h2>
            <p className="text-stone-500 leading-relaxed mb-10">
              Tell us about your event and we&apos;ll get back to you within 24 hours with a tailored proposal. No obligation, no pressure — just expert advice.
            </p>

            <div className="space-y-6">
              {[
                { icon: 'ri-time-line', title: 'Quick Response', desc: 'We reply within 24 hours with a custom proposal' },
                { icon: 'ri-money-pound-circle-line', title: 'No Hidden Fees', desc: 'Transparent pricing with everything itemised' },
                { icon: 'ri-shield-check-line', title: 'Fully Insured', desc: 'Complete public liability and equipment cover' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-lg text-amber-600`}></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-stone-900 mb-0.5">{item.title}</h4>
                    <p className="text-sm text-stone-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 p-6 bg-white rounded-2xl border border-stone-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                  <i className="ri-double-quotes-l text-amber-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-stone-900">Sarah &amp; James</p>
                  <p className="text-xs text-stone-400">Wedding, June 2024</p>
                </div>
              </div>
              <p className="text-sm text-stone-500 italic leading-relaxed">
                &ldquo;From the first consultation to the last dance, House of FMR made our wedding absolutely magical. Worth every penny.&rdquo;
              </p>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100">
            {status === 'success' ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-check-double-line text-3xl text-amber-600"></i>
                </div>
                <h3 className="text-xl font-display text-stone-900 mb-2">Thank You!</h3>
                <p className="text-stone-500 text-sm">We&apos;ve received your enquiry and will be in touch within 24 hours.</p>
              </div>
            ) : (
              <form id="services-consultation-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-stone-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="you@email.com"
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-stone-50"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+44 7XXX XXX XXX"
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-stone-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Event Type *</label>
                    <select
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-stone-50 cursor-pointer"
                    >
                      <option value="">Select type</option>
                      <option value="Wedding">Wedding</option>
                      <option value="Corporate Event">Corporate Event</option>
                      <option value="Private Party">Private Party</option>
                      <option value="Equipment Hire Only">Equipment Hire Only</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Guest Count</label>
                    <select
                      name="guestCount"
                      value={formData.guestCount}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-stone-50 cursor-pointer"
                    >
                      <option value="">Select</option>
                      <option value="Under 50">Under 50</option>
                      <option value="50-100">50–100</option>
                      <option value="100-200">100–200</option>
                      <option value="200-500">200–500</option>
                      <option value="500+">500+</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Event Date</label>
                    <input
                      type="date"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-stone-50 cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Budget</label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-stone-50 cursor-pointer"
                    >
                      <option value="">Select</option>
                      <option value="Under £2,000">Under £2,000</option>
                      <option value="£2,000-£5,000">£2,000–£5,000</option>
                      <option value="£5,000-£10,000">£5,000–£10,000</option>
                      <option value="£10,000+">£10,000+</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                    Tell Us About Your Event
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={4}
                    placeholder="Describe your vision, theme ideas, special requirements..."
                    className="w-full px-4 py-3 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-stone-50 resize-none"
                  ></textarea>
                  <p className="text-xs text-stone-300 mt-1 text-right">{formData.message.length}/500</p>
                </div>

                {status === 'error' && (
                  <div className="flex items-center gap-2 text-rose-500 text-sm">
                    <i className="ri-error-warning-line"></i>
                    Something went wrong. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-yellow-400 text-stone-900 text-sm font-semibold rounded-full hover:from-amber-400 hover:to-yellow-300 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20 disabled:opacity-60"
                >
                  {status === 'submitting' ? 'Sending...' : 'Request Free Consultation'}
                </button>

                <p className="text-xs text-stone-400 text-center">
                  No obligation. We&apos;ll respond within 24 hours.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
